import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../AppContext";
import { useApiMutation } from "./useApiMutation";
import {
  getPendingReservationIntent,
  clearPendingReservationIntent,
} from "../utilities/pendingReservationUtility";
import { showCustomConfirmDialog } from "../../components/dialogs/AppConfirmDialog";
import { dateUtility } from "../utilities/dateUtility";
import { getErrorToast, getSuccessToast } from "../utilities/toastUtility";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import {
  constructCalendarEventHref,
  constructReservationSuccessHref,
  getDefaultRoutePath,
} from "../routes/routePaths";
import { setAxiosToken } from "../helpers/axiosHelper";

/**
 * Hook that monitors user login state and checks for pending calendar reservation intents.
 * When a logged-in user with a phone number is detected, it checks for pending reservations
 * and shows a confirmation dialog to complete the reservation.
 */
export const usePendingReservationHandler = () => {
  const { currentUser, selectedStudio } = useAppContext();
  const { mutateAsync } = useApiMutation();
  const navigate = useNavigate();
  const processedRef = useRef(false);

  useEffect(() => {
    // Only check once per session
    if (processedRef.current) {
      return;
    }

    // Wait for user to be logged in with a phone number (must not be empty string)
    const hasValidPhoneNumber = !!currentUser?.phoneNumber?.trim();
    if (!currentUser || !hasValidPhoneNumber || !selectedStudio?.id) {
      return;
    }

    // Check for pending reservation intent
    const pendingIntent = getPendingReservationIntent();

    if (!pendingIntent) {
      return;
    }

    // If intent is for a different studio, clear it
    if (pendingIntent.studioId !== selectedStudio.id) {
      clearPendingReservationIntent();
      return;
    }

    // Mark as processed to prevent duplicate handling
    processedRef.current = true;

    // Format event details for display
    const eventDate = dateUtility.formatDate(new Date(pendingIntent.eventDate));
    const eventDetails = `${pendingIntent.eventName} (${eventDate}, ${pendingIntent.startTime}-${pendingIntent.endTime})`;

    // Show confirmation dialog
    const confirmMessage = `Искате ли да завършите резервацията за <strong>${eventDetails}</strong>?`;

    const openEventInCalendar = () => {
      navigate(
        constructCalendarEventHref(
          pendingIntent.studioId,
          pendingIntent.eventId,
          new Date(pendingIntent.eventDate)
        )
      );
    };

    const createReservation = async () => {
      setAxiosToken(currentUser.authToken);
      try {
        const data = await mutateAsync({
          endpoint: endpointsMetadata.portalCalendar.reserve,
          globalIsLoading: true,
          method: "post",
          disableSuccessToast: true,
          disableErrorToast: true,
          payload: {
            eventId: pendingIntent.eventId,
            userId: currentUser.id,
          },
        });

        const reservationId =
          (data as any)?.reservationId ?? Number(data);

        if (!reservationId) {
          getErrorToast("Възникна грешка при резервацията");
          openEventInCalendar();
          return;
        }

        getSuccessToast("Успешно направена резервация");
        navigate(constructReservationSuccessHref(reservationId));
      } catch (error: any) {
        const displayMessage =
          error?.response?.data?.message || "Възникна грешка при резервацията";

        showCustomConfirmDialog(
          displayMessage,
          openEventInCalendar,
          () => {},
          "warning",
          "Добре",
          null
        );
      }
    };

    showCustomConfirmDialog(
      confirmMessage,
      () => {
        clearPendingReservationIntent();
        if (pendingIntent.requirePrepayment || pendingIntent.isMultisportAvailable) {
          // Prepaid events (any provider), and events that accept Multisport — drop the
          // user on the calendar with the event preview open so they run the proper
          // payment-method dialog flow.
          openEventInCalendar();
        } else {
          // No prepayment, no Multisport — create the reservation directly.
          createReservation();
        }
      },
      () => {
        clearPendingReservationIntent();
        navigate(getDefaultRoutePath(currentUser));
      },
      "success",
      "Да",
      "Не"
    );
  }, [currentUser, currentUser?.phoneNumber, selectedStudio?.id, mutateAsync]);
};
